import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Users, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { STREAMS } from '../data/mock';
import { fetchExams } from '../lib/api';

const Exams = () => {
  const [q, setQ] = useState('');
  const [stream, setStream] = useState('all');
  const [exams, setExams] = useState([]);

  useEffect(() => {
    fetchExams().then(setExams).catch(() => setExams([]));
  }, []);

  const filtered = useMemo(() => {
    return exams.filter((e) => {
      const matchesQ = !q || e.name.toLowerCase().includes(q.toLowerCase()) || (e.full || '').toLowerCase().includes(q.toLowerCase());
      const matchesStream = stream === 'all' || e.stream === stream;
      return matchesQ && matchesStream;
    });
  }, [q, stream, exams]);

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="bg-gradient-to-br from-slate-900 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <Badge className="bg-orange-500/20 text-orange-300 hover:bg-orange-500/20 border-orange-500/30 mb-3">Entrance Exams 2025</Badge>
          <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>All exams. All you need to know.</h1>
          <p className="text-slate-300 mt-2 max-w-2xl">Dates, patterns, eligibility, cutoffs and preparation resources for every major entrance exam in India.</p>

          <div className="mt-6 max-w-xl relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search JEE, NEET, CAT, GATE..."
              className="w-full pl-11 pr-4 py-3 rounded-full text-slate-900 outline-none border border-transparent focus:ring-4 focus:ring-teal-500/40"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stream chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setStream('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${stream === 'all' ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-teal-500'}`}>
            All exams
          </button>
          {STREAMS.map((s) => (
            <button key={s.key} onClick={() => setStream(s.key)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${stream === s.key ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-teal-500'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Exam Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((e) => (
            <Link key={e.id} to={`/exams/${e.slug || e.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 card-lift">
              <div className="relative h-32 overflow-hidden">
                <img src={e.image} alt={e.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
                <div className="absolute bottom-3 left-4">
                  <div className="font-bold text-white text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{e.name}</div>
                  <div className="text-xs text-white/80">{e.full}</div>
                </div>
                <Badge className="absolute top-3 right-3 bg-white text-slate-900 hover:bg-white">{e.level}</Badge>
              </div>
              <div className="p-5">
                <p className="text-sm text-slate-600 line-clamp-2">{e.description}</p>
                <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-3.5 h-3.5 text-teal-600" /> {e.nextDate}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-3.5 h-3.5 text-teal-600" /> {e.frequency}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users className="w-3.5 h-3.5 text-teal-600" /> {e.registrations}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 font-semibold">{e.mode}</div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Full details</span>
                  <span className="text-sm font-semibold text-teal-700 group-hover:text-orange-600 inline-flex items-center gap-1">
                    View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center border border-slate-200">
            <p className="text-slate-600">No exams match your search.</p>
            <Button onClick={() => { setQ(''); setStream('all'); }} className="mt-3 bg-teal-600 hover:bg-teal-700 text-white">Reset</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Exams;
